import React, { MouseEventHandler, useCallback, useMemo, useState } from 'react'
import { connect, useSelector } from 'react-redux'

import { selectIsProjectSwitchInitiated } from '@store/actionSlices/appConfig'
import { selectProjectIdentity } from '@store/actionSlices/projectIdentity'
import { ProjectIdentity, RootStateTypeExtra } from '@store/types'

import useProjectSwitchUtils from '@utilities/useProjectSwitchUtils'

import FirebaseControlQuery from '@firebaseUtil/firebase-control-query'

import { ProjectType } from '@adUtilities/constants'
import useMapActionRouteUtils from '@adUtilities/map-action-route-utils'
import { notifyError } from '@adUtilities/notifier'
import { DefaultRouteParamKey } from '@adUtilities/types'

import Layer from './layer'

interface LayerApartmentProps {
  projectIdentity: ProjectIdentity
  label: string
  actionRoute: string
}

function LayerApartment({
  projectIdentity,
  label,
  actionRoute,
}: LayerApartmentProps) {
  const firebaseControlQuery = FirebaseControlQuery({
    ...projectIdentity,
    sessionKey: projectIdentity.sessionId,
  })

  const { type: currentProjectType } = useSelector(selectProjectIdentity)

  const { parseActionRoute } = useMapActionRouteUtils({
    currentProjectType,
  })

  const { isValid, route, routeParams, projectType } = useMemo(
    () => parseActionRoute(actionRoute),
    [actionRoute, parseActionRoute]
  )

  const { startProjectSwitch } = useProjectSwitchUtils()
  const isRefreshing = useSelector(selectIsProjectSwitchInitiated)
  const [isProjectSwitching, setIsProjectSwitching] = useState(false)

  const hasValidProjectSwitchParam = isValid && !!routeParams.project

  const handleProjectSwitch = useCallback(async () => {
    if (!isValid) {
      return
    }
    const project = routeParams[DefaultRouteParamKey.Project]

    if (!project) {
      return
    }

    setIsProjectSwitching(true)
    const { status, message } = await startProjectSwitch({
      projectCanonicalName: project,
      redirectTo: String(route),
      redirectContext: routeParams,
    })
    setIsProjectSwitching(false)

    if (!status && message) {
      notifyError(message)
    }
  }, [startProjectSwitch, route, routeParams])

  const handleClick = useCallback<MouseEventHandler>(
    async (e) => {
      e.preventDefault()

      if (isRefreshing || isProjectSwitching || !isValid) {
        return
      }

      if (hasValidProjectSwitchParam && routeParams.project) {
        handleProjectSwitch()
        return
      }

      if (projectType !== ProjectType.Apartment) {
        return
      }

      firebaseControlQuery.update({
        [`building.activeBlock`]: routeParams.block || '',
        [`building.activeLevel`]: routeParams.level || '',
        [`building.activeUnit`]: routeParams.unit || '',
        [`activeRoute`]: route,
      })
    },
    [
      handleProjectSwitch,
      projectType,
      route,
      routeParams,
      isValid,
      isRefreshing,
      isProjectSwitching,
      hasValidProjectSwitchParam,
    ]
  )

  return (
    <Layer
      label={label}
      route={(isValid && route) || undefined}
      isValid={isValid}
      onClick={handleClick}
      isProcessing={isProjectSwitching}
      as={hasValidProjectSwitchParam ? 'button' : undefined}
    />
  )
}

export default connect(({ projectIdentity }: RootStateTypeExtra) => ({
  projectIdentity,
}))(LayerApartment)
